import { type ApiSchemas, call, newIdempotencyKey } from '@rental/contracts/client'
import type { EndingKind } from '@rental/domain'
import { format, formatDateTime, formatEuro, formatTime, plural } from '@rental/i18n'
import { Button, Field, Notice, Textarea } from '@rental/ui'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { useAccess } from '../access-context'
import { api, load } from '../api'
import { canDo } from '../booking-can'
import { ChoiceChips } from '../components/ChoiceChips'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { OutcomeFocus } from '../components/OutcomeNotice'
import { FIELD, FIELD_ROW, Loading, ProblemNotice, StaffInput } from '../components/ui'
import { useLocale } from '../i18n'
import { problemOf, type StaffProblem } from '../problems'
import { refreshAfterChange } from '../refresh'
import { EMPTY_ENDING, type EndingInput, endingDraft } from './ending'

type Detail = ApiSchemas['StaffBookingDetail']
type Preview = ApiSchemas['CancellationPreview']
type Done = ApiSchemas['LifecycleDone']

/** What the last cancel, no-show or close did, shown on the booking until the next. */
export interface LifecycleOutcome {
  kind: 'cancelled' | 'no_show' | 'closed'
  /** Stripe did not answer: the customer's page may still take money until then. */
  onlinePageOpenUntil: string | null
  /** The customer had just paid online: recorded, and shown as money to give back. */
  paidOnlineMeanwhile: boolean
}

/**
 * S3 (RES-2, RES-5, Q-13): the ways a booking ends, for the roles that may.
 * The server decides again on every request; this only offers what makes sense
 * now. Cancelling is offered while the booking is confirmed, so a held deposit
 * is explained in the dialog rather than by a missing button.
 */
export function LifecycleActions({
  detail,
  onDone,
}: {
  detail: Detail
  onDone: (outcome: LifecycleOutcome) => void
}) {
  const may = canDo(detail.can)
  const { booking, lifecycle } = detail
  const confirmed = booking.status === 'confirmed'
  // W2-S S4: a website draft waiting for its online share is cancelled, never a no-show.
  const draft = booking.status === 'draft' && lifecycle.canCancel
  // A no-show is marked from the booked pick-up; the dialog checks with the server's clock.
  const pickupDue = Date.now() >= new Date(booking.startsAt).getTime()
  return (
    <>
      {(confirmed || draft) && may('cancel') ? (
        <EndBookingAction code={booking.code} kind="cancel" unconfirmed={draft} onDone={onDone} />
      ) : null}
      {confirmed && pickupDue && may('cancel') ? (
        <EndBookingAction code={booking.code} kind="no_show" onDone={onDone} />
      ) : null}
      {booking.status === 'returned' && lifecycle.close.allowed && may('close') ? (
        <CloseAction code={booking.code} onDone={onDone} />
      ) : null}
    </>
  )
}

/** Q-13: what still stands between a returned booking and its closing. */
export function CloseHint({ detail }: { detail: Detail }) {
  const { messages: m } = useLocale()
  const { booking, lifecycle } = detail
  if (booking.status !== 'returned' || lifecycle.close.allowed) return null
  if (!canDo(detail.can)('close')) return null
  return (
    <div className="text-sm text-text-muted">
      <p>{m.lifecycle.closeBlocked}</p>
      <ul className="list-disc ps-6">
        {lifecycle.close.blocks.map((block) => (
          <li key={block}>{m.lifecycle.closeBlocks[block]}</li>
        ))}
      </ul>
    </div>
  )
}

/** The page's answer to the last ending: done, and anything Stripe left open. */
export function LifecycleNotices({ outcome }: { outcome: LifecycleOutcome | null }) {
  const { locale, messages: m } = useLocale()
  if (!outcome) return null
  // The booking has ended and its buttons with it: what was done takes the focus.
  return (
    <OutcomeFocus key={outcome.kind}>
      <Notice tone="success">
        <p>{m.lifecycle.done[outcome.kind]}</p>
      </Notice>
      {outcome.paidOnlineMeanwhile ? (
        <Notice tone="warning">
          <p>{m.lifecycle.paidOnlineMeanwhile}</p>
        </Notice>
      ) : null}
      {outcome.onlinePageOpenUntil ? (
        <Notice tone="warning">
          <p>
            {format(m.payment.pageOpen, { time: formatTime(locale, outcome.onlinePageOpenUntil) })}
          </p>
        </Notice>
      ) : null}
    </OutcomeFocus>
  )
}

/** How the booking ended, when and by whom; the cancellation's reason. */
export function EndingFacts({ ending }: { ending: NonNullable<Detail['ending']> }) {
  const { locale, messages: m } = useLocale()
  const date = formatDateTime(locale, ending.at)
  return (
    // How it ended is a fact, not a fault: neutral, as its chip (BookingItem's tone map).
    <div className="flex flex-col gap-1 text-sm">
      <p>
        {ending.by
          ? format(m.lifecycle.ended[ending.kind], { date, name: ending.by })
          : format(m.lifecycle.endedAt[ending.kind], { date })}
      </p>
      {ending.reason ? (
        <p className="whitespace-pre-line text-text">
          {format(m.lifecycle.reasonLine, { reason: ending.reason })}
        </p>
      ) : null}
    </div>
  )
}

function doneOutcome(kind: LifecycleOutcome['kind'], data: Done): LifecycleOutcome {
  return {
    kind,
    onlinePageOpenUntil: data.onlinePageOpenUntil,
    paidOnlineMeanwhile: data.paidOnlineMeanwhile,
  }
}

/**
 * RES-5: cancel, or mark a no-show, with the fee in view. The fee is the
 * terms' (checked again by the server against what this dialog showed), a
 * lower one with a reason for those who may change prices, or typed while the
 * terms say nothing (Q-27).
 */
function EndBookingAction({
  code,
  kind,
  unconfirmed = false,
  onDone,
}: {
  code: string
  kind: EndingKind
  /** W2-S S4: a draft, never confirmed: cancelled with no fee, whatever its terms say. */
  unconfirmed?: boolean
  onDone: (outcome: LifecycleOutcome) => void
}) {
  const { messages: m } = useLocale()
  const { takeOver } = useAccess()
  const queryClient = useQueryClient()
  const dialog = useRef<HTMLDialogElement>(null)
  const [open, setOpen] = useState(false)
  const [input, setInput] = useState<EndingInput>(EMPTY_ENDING)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<StaffProblem | null>(null)
  const lastSent = useRef<{ body: string; key: string } | null>(null)
  const previewKey = ['cancellation', code, kind]
  const preview = useQuery({
    queryKey: previewKey,
    queryFn: () =>
      load(
        call(
          api.GET('/v1/staff/bookings/{code}/cancellation', {
            params: { path: { code }, query: { kind } },
          }),
        ),
      ),
    enabled: open,
  })
  const draft = preview.data ? endingDraft(preview.data, input) : null

  function show() {
    // Never the figures of an earlier opening: the fee depends on the time.
    queryClient.removeQueries({ queryKey: previewKey })
    setInput(EMPTY_ENDING)
    setProblem(null)
    lastSent.current = null
    setOpen(true)
    dialog.current?.showModal()
  }

  async function submit() {
    if (!draft?.body) return
    setProblem(null)
    // A retry of the same answer replays; a changed one gets its own key.
    const serialized = JSON.stringify(draft.body)
    if (lastSent.current?.body !== serialized) {
      lastSent.current = { body: serialized, key: newIdempotencyKey() }
    }
    const params = { path: { code }, header: { 'idempotency-key': lastSent.current.key } }
    setBusy(true)
    const result =
      kind === 'cancel'
        ? await call(
            api.POST('/v1/staff/bookings/{code}/cancel', {
              params,
              body: draft.body as ApiSchemas['CancelRequest'],
            }),
          )
        : await call(api.POST('/v1/staff/bookings/{code}/no-show', { params, body: draft.body }))
    setBusy(false)
    if (result.ok) {
      lastSent.current = null
      dialog.current?.close()
      onDone(doneOutcome(kind === 'cancel' ? 'cancelled' : 'no_show', result.data))
      // The car is free: the Planning, Today and the lists must say so at once.
      await refreshAfterChange(queryClient, code)
      return
    }
    if (takeOver(result.problem)) return
    setProblem(result.problem)
    // Whatever changed meanwhile (a tier passed, the booking moved on), show it as it is now.
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: previewKey }),
      queryClient.invalidateQueries({ queryKey: ['booking', code] }),
    ])
  }

  const previewProblem = problemOf(preview.error)
  return (
    <>
      <Button variant="outline" className="rounded-sm" onClick={show}>
        {kind === 'cancel' ? m.lifecycle.cancel : m.lifecycle.noShow}
      </Button>
      <ConfirmDialog
        dialogRef={dialog}
        className="max-w-lg"
        title={format(kind === 'cancel' ? m.lifecycle.cancelTitle : m.lifecycle.noShowTitle, {
          code,
        })}
        confirmLabel={kind === 'cancel' ? m.lifecycle.confirmCancel : m.lifecycle.confirmNoShow}
        cancelLabel={m.lifecycle.back}
        busy={busy}
        ready={Boolean(draft?.body)}
        reasons={draft ? draft.waits.map((wait) => m.lifecycle.why[wait]) : []}
        onConfirm={() => void submit()}
        onClose={() => setOpen(false)}
      >
        {open && preview.data && draft ? (
          <EndingForm
            preview={preview.data}
            input={input}
            onInput={setInput}
            draft={draft}
            unconfirmed={unconfirmed}
          />
        ) : open && previewProblem ? (
          <ProblemNotice problem={previewProblem} />
        ) : open ? (
          <Loading />
        ) : null}
        <ProblemNotice problem={problem} />
      </ConfirmDialog>
    </>
  )
}

function EndingForm({
  preview,
  input,
  onInput,
  draft,
  unconfirmed = false,
}: {
  preview: Preview
  input: EndingInput
  onInput: (input: EndingInput) => void
  draft: ReturnType<typeof endingDraft>
  unconfirmed?: boolean
}) {
  const { locale, messages: m } = useLocale()
  const set = (patch: Partial<EndingInput>) => onInput({ ...input, ...patch })
  const euro = (cents: number) => formatEuro(cents, locale)
  const computed = preview.fee.computedCents
  const canLower = computed !== null && computed > 0 && preview.canOverride
  const lowering = canLower && input.choice === 'override'
  const pickupAt = new Date(preview.startsAt).getTime()

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <p>{format(m.lifecycle.pickup, { date: formatDateTime(locale, preview.startsAt) })}</p>
        {preview.kind === 'cancel' ? (
          <p className="text-sm text-text-muted">
            {Date.now() >= pickupAt
              ? m.lifecycle.pickupPassed
              : preview.fee.hoursLeft > 0
                ? plural(locale, m.lifecycle.hoursLeft, preview.fee.hoursLeft)
                : m.lifecycle.underAnHour}
          </p>
        ) : null}
      </div>

      {preview.allowed ? null : (
        <Notice tone="warning">
          <ul className="flex flex-col gap-1">
            {preview.blocks.map((block) => (
              <li key={block}>
                {format(m.lifecycle.blocks[block], {
                  time: formatDateTime(locale, preview.startsAt),
                })}
              </li>
            ))}
          </ul>
        </Notice>
      )}

      {preview.allowed ? (
        <>
          {computed !== null ? (
            <div className="flex flex-col gap-2">
              <p className="font-medium">
                {unconfirmed
                  ? m.lifecycle.unconfirmedNoFee
                  : format(m.lifecycle.policyFee, {
                      version: preview.policy.termsVersion ?? '—',
                      amount: euro(computed),
                    })}
              </p>
              {preview.kind === 'cancel' && preview.fee.tierHoursBefore !== null ? (
                <p className="text-sm text-text-muted">
                  {preview.fee.tierHoursBefore > 0
                    ? plural(locale, m.lifecycle.tier, preview.fee.tierHoursBefore)
                    : m.lifecycle.lastTier}
                </p>
              ) : null}
              {preview.policy.accepted ? null : (
                <Notice tone="warning">
                  <p>{m.lifecycle.notAccepted}</p>
                </Notice>
              )}
              {canLower ? (
                <ChoiceChips
                  name={`fee-choice-${preview.kind}`}
                  label={m.lifecycle.feeChoice}
                  options={[
                    {
                      value: 'policy',
                      label: format(m.lifecycle.feePolicy, { amount: euro(computed) }),
                    },
                    { value: 'override', label: m.lifecycle.feeOverride },
                  ]}
                  value={input.choice}
                  onChange={(choice) => set({ choice })}
                />
              ) : null}
              {lowering ? (
                <div className={FIELD_ROW}>
                  <Field
                    className={FIELD}
                    label={m.lifecycle.overrideAmount}
                    hint={format(m.lifecycle.overrideAmountHint, { amount: euro(computed) })}
                    required
                    error={
                      draft.problems.amount
                        ? format(m.lifecycle.overrideTooHigh, { amount: euro(computed) })
                        : null
                    }
                  >
                    {(control) => (
                      <StaffInput
                        {...control}
                        inputMode="decimal"
                        autoComplete="off"
                        value={input.amount}
                        onChange={(event) => set({ amount: event.target.value })}
                      />
                    )}
                  </Field>
                  <Field
                    className={FIELD}
                    label={m.lifecycle.overrideReason}
                    hint={m.lifecycle.overrideReasonHint}
                    required
                    error={draft.problems.feeReason ? m.lifecycle.reasonShort : null}
                  >
                    {(control) => (
                      <StaffInput
                        {...control}
                        maxLength={500}
                        autoComplete="off"
                        value={input.feeReason}
                        onChange={(event) => set({ feeReason: event.target.value })}
                      />
                    )}
                  </Field>
                </div>
              ) : null}
            </div>
          ) : (
            <>
              <Notice tone="warning">
                <p>{m.lifecycle.noPolicy}</p>
              </Notice>
              <Field
                label={m.lifecycle.typedAmount}
                hint={format(m.lifecycle.typedAmountHint, { amount: euro(preview.totalCents) })}
                required
                error={
                  draft.problems.amount
                    ? format(m.lifecycle.amountInvalid, { amount: euro(preview.totalCents) })
                    : null
                }
              >
                {(control) => (
                  <StaffInput
                    {...control}
                    inputMode="decimal"
                    autoComplete="off"
                    value={input.amount}
                    onChange={(event) => set({ amount: event.target.value })}
                  />
                )}
              </Field>
            </>
          )}

          <Field
            label={preview.kind === 'cancel' ? m.lifecycle.reason : m.lifecycle.note}
            hint={preview.kind === 'cancel' ? m.lifecycle.reasonHint : undefined}
            required={preview.kind === 'cancel'}
            {...(preview.kind === 'cancel' ? {} : { optionalLabel: m.newBooking.optional })}
            error={draft.problems.reason ? m.lifecycle.reasonShort : null}
          >
            {(control) => (
              <Textarea
                {...control}
                className="rounded-sm"
                rows={2}
                maxLength={500}
                value={input.reason}
                onChange={(event) => set({ reason: event.target.value })}
              />
            )}
          </Field>

          <div className="flex flex-col gap-1">
            <p>{format(m.lifecycle.paidSoFar, { amount: euro(preview.paidCents) })}</p>
            {draft.afterCents === null ? null : (
              <p className="font-medium" data-testid="after-ending">
                {draft.afterCents > 0
                  ? format(m.lifecycle.afterOwed, { amount: euro(draft.afterCents) })
                  : draft.afterCents < 0
                    ? format(m.lifecycle.afterCredit, { amount: euro(-draft.afterCents) })
                    : m.lifecycle.afterSettled}
              </p>
            )}
            <p className="text-sm text-text-muted">{m.lifecycle.carFreed}</p>
          </div>
        </>
      ) : null}
    </div>
  )
}

/** Q-13: close a returned booking once nothing is left to settle. */
function CloseAction({
  code,
  onDone,
}: {
  code: string
  onDone: (outcome: LifecycleOutcome) => void
}) {
  const { messages: m } = useLocale()
  const { takeOver } = useAccess()
  const queryClient = useQueryClient()
  const dialog = useRef<HTMLDialogElement>(null)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<StaffProblem | null>(null)
  // One key per opening: a retry after a lost answer replays the close.
  const key = useRef<string | null>(null)

  function show() {
    setProblem(null)
    key.current = newIdempotencyKey()
    dialog.current?.showModal()
  }

  async function submit() {
    if (!key.current) key.current = newIdempotencyKey()
    setProblem(null)
    setBusy(true)
    const result = await call(
      api.POST('/v1/staff/bookings/{code}/close', {
        params: { path: { code }, header: { 'idempotency-key': key.current } },
      }),
    )
    setBusy(false)
    if (result.ok) {
      dialog.current?.close()
      onDone(doneOutcome('closed', result.data))
      await refreshAfterChange(queryClient, code)
      return
    }
    if (takeOver(result.problem)) return
    setProblem(result.problem)
    // A payment found online, a refund on its way: the account shows it now.
    await queryClient.invalidateQueries({ queryKey: ['booking', code] })
  }

  return (
    <>
      <Button variant="staff" onClick={show}>
        {m.lifecycle.close}
      </Button>
      <ConfirmDialog
        dialogRef={dialog}
        title={format(m.lifecycle.closeTitle, { code })}
        confirmLabel={m.lifecycle.confirmClose}
        cancelLabel={m.lifecycle.back}
        busy={busy}
        onConfirm={() => void submit()}
      >
        <p>{m.lifecycle.closeBody}</p>
        <ProblemNotice problem={problem} />
      </ConfirmDialog>
    </>
  )
}
